import dayjs from "dayjs";
import { LloFunnelReportItem } from "../../../../api";
import { CASE_LEVEL_COLUMNS } from "./constants";
import { stripHtml } from "./htmlUtils";
import type { RowWithSpans, SortableColumn, SortDirection, SortRule } from "./types";
import { DEFAULT_SORT_RULES } from "./types";

function getMainFunnelTabOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  const prefix = "Main Funnel — ";
  if (!value.startsWith(prefix)) {
    return 99;
  }

  const tab = value.slice(prefix.length);
  const order: Record<string, number> = {
    Complaint: 0,
    "Discovery Requests": 1,
    "Plaintiff's Discovery Responses": 2,
    "Judicial Assignment Responsive": 3,
    "Case Management Conference": 4,
  };

  return order[tab] ?? 99;
}

function getFunnelCategoryOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  if (value.startsWith("Main Funnel")) return 0;
  if (value.startsWith("Pre-Lit Funnel")) return 1;
  if (value.startsWith("Litigation Funnel")) return 2;
  if (value.startsWith("Secondary Funnel")) return 3;
  if (value.startsWith("Single Task")) return 4;
  if (value === "Manual") return 5;
  if (value.startsWith("None")) return 6;
  return 7;
}

function getSortValue(row: LloFunnelReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "raa":
      return row.raa?.toLowerCase() ?? "";
    case "ap":
      return row.ap?.toLowerCase() ?? "";
    case "upcomingDeadlines":
      return row.upcomingDeadlines?.toLowerCase() ?? "";
    case "lloExpiration":
      return row.lloExpiration?.toLowerCase() ?? "";
    case "negotiation":
      return row.negotiation?.toLowerCase() ?? "";
    case "funnel":
      return `${getFunnelCategoryOrder(row.funnel)}|${getMainFunnelTabOrder(row.funnel)}|${row.funnel?.toLowerCase() ?? ""}`;
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "taskAssignee":
      return row.taskAssignee?.toLowerCase() ?? "";
    case "description":
      return stripHtml(row.description ?? "").toLowerCase();
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    default:
      return "";
  }
}

function compareSortValues(
  aValue: string | number,
  bValue: string | number,
  order: SortDirection
) {
  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;
  return 0;
}

function getDefaultTaskOrder(a: LloFunnelReportItem, b: LloFunnelReportItem) {
  const categoryOrder = getFunnelCategoryOrder(a.funnel) - getFunnelCategoryOrder(b.funnel);
  if (categoryOrder !== 0) return categoryOrder;

  const tabOrder = getMainFunnelTabOrder(a.funnel) - getMainFunnelTabOrder(b.funnel);
  if (tabOrder !== 0) return tabOrder;

  const funnelCompare = (a.funnel ?? "").localeCompare(b.funnel ?? "");
  if (funnelCompare !== 0) return funnelCompare;

  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  return aDue - bDue;
}

function compareByRules(
  a: LloFunnelReportItem,
  b: LloFunnelReportItem,
  rules: SortRule[]
) {
  for (const rule of rules) {
    const result = compareSortValues(
      getSortValue(a, rule.column),
      getSortValue(b, rule.column),
      rule.direction
    );
    if (result !== 0) return result;
  }
  return getDefaultTaskOrder(a, b);
}

function getGroupSortValue(group: LloFunnelReportItem[], column: SortableColumn): string | number {
  const first = group[0];
  if (!first) return "";

  if (CASE_LEVEL_COLUMNS.includes(column)) {
    return getSortValue(first, column);
  }

  if (column === "dueDate") {
    return Math.min(
      ...group.map((row) => (row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER))
    );
  }

  return group.reduce<string | number>((best, row) => {
    const value = getSortValue(row, column);
    if (best === "") return value;
    return compareSortValues(value, best, "asc") < 0 ? value : best;
  }, "");
}

function compareGroupsByRules(groupA: LloFunnelReportItem[], groupB: LloFunnelReportItem[], rules: SortRule[]) {
  for (const rule of rules) {
    const result = compareSortValues(
      getGroupSortValue(groupA, rule.column),
      getGroupSortValue(groupB, rule.column),
      rule.direction
    );
    if (result !== 0) return result;
  }
  return (groupA[0]?.caseName ?? "").localeCompare(groupB[0]?.caseName ?? "");
}

export function groupRowsByCase(rows: LloFunnelReportItem[]): LloFunnelReportItem[][] {
  const groups: LloFunnelReportItem[][] = [];
  let currentGroup: LloFunnelReportItem[] = [];
  let currentCaseName = "";

  rows.forEach((row) => {
    if (row.caseName !== currentCaseName) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [row];
      currentCaseName = row.caseName ?? "";
    } else {
      currentGroup.push(row);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

function normalizeSortRules(rules: SortRule[] | undefined): SortRule[] {
  if (!rules?.length) return [...DEFAULT_SORT_RULES];
  const seen = new Set<SortableColumn>();
  const normalized: SortRule[] = [];
  rules.forEach((rule) => {
    if (seen.has(rule.column)) return;
    seen.add(rule.column);
    normalized.push(rule);
  });
  return normalized.length > 0 ? normalized : [...DEFAULT_SORT_RULES];
}

export function sortReportRows(
  rows: LloFunnelReportItem[],
  rules: SortRule[]
): LloFunnelReportItem[] {
  const sortRules = normalizeSortRules(rules);
  const groups = groupRowsByCase(rows);

  groups.sort((groupA, groupB) => compareGroupsByRules(groupA, groupB, sortRules));

  const primaryIsCaseLevel = CASE_LEVEL_COLUMNS.includes(sortRules[0]?.column ?? "caseName");

  return groups.flatMap((group) =>
    [...group].sort((a, b) =>
      primaryIsCaseLevel && sortRules.every((rule) => CASE_LEVEL_COLUMNS.includes(rule.column))
        ? getDefaultTaskOrder(a, b)
        : compareByRules(a, b, sortRules)
    )
  );
}

export function computeCaseGroupSpans(rows: LloFunnelReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
        isLastInCaseGroup: k === j - 1,
      });
    }

    i = j;
  }

  return out;
}
